'use client';

/**
 * Manages the opening and closing of the job details slide-out panel.
 *
 * This component is responsible for handling the logic to open and close the job details
 *  slide-out panel based on the current URL
 * and user interactions.
 * It listens for the 'openJobSidePanel' event and updates the URL and state accordingly.
 * The slide-out panel is only displayed if the current URL matches
 *  one of the 'OPEN_SIDE_PANEL_ROUTES'.
* Stores job ID or MR ID in Redux state for making activity log api calls for job details
  simultaneously with basic info as the job details activity log api needs MR ID
  */
import { useCallback, useEffect } from 'react';
import { useAppDispatch } from 'src/hooks/redux-hooks';
import { openJobDetailsSlideOut } from 'src/redux/jobs/slice';
import JobSlideOut from 'src/components/job-slide-out';


const OPEN_SIDE_PANEL_ROUTES = [
  'open-jobs',
  'dashboard',
  'jobs',
  'open-make-readies',
  'closed-make-readies',
  'quotes',
  'invoices',
  'help',
  'customer-service-agreement',
];
const JobDetailsSlideOutContainer = () => {
  const dispatch = useAppDispatch();

  const toOpenJobSidePanel = useCallback((event?: Event | CustomEvent) => {
    const searchParam = new URLSearchParams(window.location.search);
    // to open slide-out based on type of job
    const jobId = searchParam?.get('jobId');
    const srId = searchParam?.get('mr');
    const customEvent = event as CustomEvent<{
      type?: string;
      activeItemNumber?: string | number;
      jobId?: string;
      srId?: number;
      activeTab?: string;
    }> | undefined;
    const type = (customEvent?.detail?.type) || (jobId ? 'jobDetails' : 'serviceRequestDetails');

    if (
      OPEN_SIDE_PANEL_ROUTES?.some((item) => window?.location?.pathname?.includes(item))
      && (jobId || srId)
    ) {
      dispatch(openJobDetailsSlideOut({
        openedJobDetailsId: String(customEvent?.detail?.activeItemNumber || ((type === 'jobDetails' ? jobId : srId) || '')),
        type,
        // jobId is only meaningful for 'jobDetails' — the job-details response always
        // overwrites it with root_node_id for that type and with '' for
        // 'serviceRequestDetails', so seed the same value here to avoid a spurious change.
        jobId: type === 'jobDetails' ? String(customEvent?.detail?.jobId || '') : '',
        srId: String(customEvent?.detail?.srId || ''),
        activeTab: customEvent?.detail?.activeTab || '',
        hasInitialActivityLogApiTriggered: false,
      }));
    } else {
      dispatch(openJobDetailsSlideOut({
        openedJobDetailsId: '',
        type: '',
        jobId: '',
        srId: '',
        activeTab: '',
        hasInitialActivityLogApiTriggered: false,
      }));
    }
  }, [dispatch]);

  useEffect(() => {
    document.addEventListener('openJobSidePanel', toOpenJobSidePanel);
    // A browser event that fires when the user navigates through browser history (back/forward)
    window.addEventListener('popstate', toOpenJobSidePanel);
    toOpenJobSidePanel();

    return () => {
      document.removeEventListener('openJobSidePanel', toOpenJobSidePanel);
      window.removeEventListener('popstate', toOpenJobSidePanel);
    };
  }, [toOpenJobSidePanel]);

  return (
    <JobSlideOut />
  );
};

export default JobDetailsSlideOutContainer;
